/**
 * toolSearch.js — what the Tools quick search (Ctrl+K, components/ToolsQuickSearch.jsx) lists: the
 * official tools and the person's own agent-built ones together (2026-10-02).
 *
 * The order mirrors the Tools rail: the pinned official tool (Ask Clara AI), the saved tools in the
 * person's sidebar (rail order), the other official tools in registry order, then the person's other
 * saved tools (own first, then team — the server's `available` order). A query keeps that order and
 * filters by name / description (and the slug of an official tool). Without one, recently opened tools
 * come first — toolsMru holds official ids and `my:<id>` keys for saved tools.
 *
 * Entries: official tools keep their registry fields plus { key: id, kind: 'tool' }; a saved tool is
 * { key: 'my:<id>', kind: 'mine', id, name, description, mine, owner }.
 */
import { userToolKey } from './toolsMru';

const savedEntry = (t) => ({ key: userToolKey(t.id), kind: 'mine', id: t.id, name: t.name, description: t.description || '', mine: !!t.mine, owner: t.owner || null });

export function toolSearchEntries({ official = [], rail = [], available = [], query = '', mru = [] } = {}) {
  const tools = official.map((t) => ({ ...t, key: t.id, kind: 'tool' }));
  const inRail = new Set(rail.map((t) => t.id));
  // rail entries first: an admin may keep somebody else's tool there, which `available` does not list
  const others = [];
  const seen = new Set(inRail);
  available.forEach((t) => { if (!seen.has(t.id)) { seen.add(t.id); others.push(t); } });
  const ordered = [
    ...tools.filter((t) => t.pinned),
    ...rail.map(savedEntry),
    ...tools.filter((t) => !t.pinned),
    ...others.map(savedEntry),
  ];

  const q = String(query || '').trim().toLowerCase();
  if (q) {
    return ordered.filter((t) => t.name.toLowerCase().includes(q)
      || (t.description || '').toLowerCase().includes(q)
      || (t.kind === 'tool' && t.id.toLowerCase().includes(q)));
  }
  const byKey = new Map(ordered.map((t) => [t.key, t]));
  const recent = mru.map((k) => byKey.get(k)).filter(Boolean);
  const recentKeys = new Set(recent.map((t) => t.key));
  return [...recent, ...ordered.filter((t) => !recentKeys.has(t.key))];
}
